import './index.css'

import {
  Badge,
  Flex,
  Flex as RadixFlex,
  IconButton,
  Theme,
  Tooltip as RadixTooltip,
} from '@radix-ui/themes'
import cn from 'clsx'
import flatMap from 'lodash/flatMap'
import React from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import {
  AUTO_TAGGING_UNLIMITED,
  BRIEFS,
  BROWSE_LIBRARIES,
  CHANNELS_UNLIMITED,
  IS_ARTIST,
  LIVE_LIBRARIES,
  WATERMARKING,
} from '@disco/constants/FeatureTypes'
import {
  AUTO_TAGGING,
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  INBOX_TYPES,
  MAX_CHANNELS_LIMITED_MODE,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_LIBRARY,
  TERRITORY,
} from '@disco/constants/GroupTypes'
import { CHANNEL_CUSTOM_SORT } from '@disco/constants/SortTypes'
import {
  openSidebar,
  setIsOnboarding,
  toggleSidebar,
  updatePageChangeSettings,
} from '@disco/state-management/actions/AppActions'
import {
  loadBriefs,
  loadLibrary,
} from '@disco/state-management/actions/ExplorerActions'
import { getSearchResults } from '@disco/state-management/actions/SearchActions'
import { updateGroup } from '@disco/state-management/actions/GroupActions'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import type { ClientLibrariesState } from '@disco/state-management/reducers/client-libraries'
import { selectBrowseFilterState } from '@disco/state-management/reducers/search-page'
import {
  getIsOnboarding,
  getIsOnboardingEnabled,
  isDeviceTypePhone,
} from '@disco/state-management/selectors/app'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import { getHasTracks } from '@disco/state-management/selectors/explorer'
// TODO: clean-up DefaultLibrariesList, CustomLibrariesList
// import CustomLibrariesList from './custom-libraries'
// import SidebarDropWrapper from 'containers/SidebarDropWrapper'
import {
  getFilteredGroupsWithNestingAndSorting,
  getGroupsCount,
  getGroupsLoading,
  getUserLibraries,
} from '@disco/state-management/selectors/groups'
import { getTrackPlaying } from '@disco/state-management/selectors/media'
import { getUserInboxesNotificationsCounter } from '@disco/state-management/selectors/notifications'
import { getChannelsSearchTerm } from '@disco/state-management/selectors/search'
import { getTracks } from '@disco/state-management/selectors/tracks'
import {
  getChannelsSortParam,
  getConstanceSettings,
  getConstanceSetting,
} from '@disco/state-management/selectors/user'

import { isChannelShared } from '@disco/utils/groups'
import { checkSetFilters } from '@disco/utils/search'
import { formatNumber } from '@disco/utils/string-utils'

import type {
  Group,
  GroupId,
  GroupWithNesting,
  IndentedGroup,
} from 'types/group'
import type { User } from 'types/user'

import GroupItemActions from 'components/ellipsis-menus/group-item-actions'
import ContactUsLink from 'components/ui/contact-us-link'
import Logo from 'components/ui/logo'
import { LuPanelLeftClose } from '@react-icons/all-files/lu/LuPanelLeftClose'
import { LuPanelLeftOpen } from '@react-icons/all-files/lu/LuPanelLeftOpen'
import CustomScrollbars from 'components/ui/scrollbar'
import Tooltip from 'components/ui/tooltip'

import CustomGroupDragWrapper from 'containers/CustomGroupDragWrapper'
import CustomGroupDropWrapper from 'containers/CustomGroupDropWrapper'
import SidebarDropWrapper from 'containers/SidebarDropWrapper'

import type { RootState } from 'store/configureStore'

import SidebarChannelTitle from './sidebar-channel-title'
import SidebarItem from './sidebar-item'
import SidebarSection, { listLink as sidebarListLink } from './sidebar-section'
import SidebarTitle from './sidebar-title'
import { SwitcherPopover } from './switcher-popover'

function mapStateToProps(state: RootState) {
  // TODO: rework Sidebar, remove useless makeIsFeatureEnabled
  const isFeatureEnabled1 = makeIsFeatureEnabled()
  const channelsUnlimited = isFeatureEnabled1(state, {
    featureName: CHANNELS_UNLIMITED,
  })
  const isFeatureEnabled2 = makeIsFeatureEnabled()
  const watermarkingEnabled = isFeatureEnabled2(state, {
    featureName: WATERMARKING,
  })
  const isFeatureEnabled3 = makeIsFeatureEnabled()
  const browseLibrariesEnabled = isFeatureEnabled3(state, {
    featureName: BROWSE_LIBRARIES,
  })
  const isFeatureEnabled4 = makeIsFeatureEnabled()
  const autoTaggingUnlimitedEnabled = isFeatureEnabled4(state, {
    featureName: AUTO_TAGGING_UNLIMITED,
  })
  const isFeatureEnabled5 = makeIsFeatureEnabled()
  const isArtist = isFeatureEnabled5(state, {
    featureName: IS_ARTIST,
  })
  const isFeatureEnabled6 = makeIsFeatureEnabled()
  const liveLibrariesEnabled = isFeatureEnabled6(state, {
    featureName: LIVE_LIBRARIES,
  })

  const constanceSettings = getConstanceSettings(state)
  const briefsEnabled = constanceSettings?.BRIEFS ?? false
  // Null while the Browse filters flag is off
  const browseFilterState = selectBrowseFilterState(state)

  return {
    app: state.app,
    user: state.user,
    onboardingEnabled: getIsOnboardingEnabled(state),
    isOnboarding: getIsOnboarding(state),
    channelSort: getChannelsSortParam(state),
    channelSearchTerm: getChannelsSearchTerm(state),
    // channels is loaded into preloadDataOnLogin
    channels: getFilteredGroupsWithNestingAndSorting(state),
    groupsCount: getGroupsCount(state),
    groupsFetched: state.groups.isFetched,
    groupsError: state.groups.error,
    libraries: getUserLibraries(state),
    groupsLoading: getGroupsLoading(state),
    isPhone: isDeviceTypePhone(state),
    playerVisible: Boolean(getTrackPlaying(state)),
    channelsLimited: !channelsUnlimited,
    // clientLibraries is loaded into preloadDataOnLogin
    clientLibraries: state.clientLibraries,
    watermarkingEnabled,
    browseLibrariesEnabled,
    autoTaggingUnlimitedEnabled,
    isArtist,
    liveLibrariesEnabled,
    briefsEnabled,
    userInboxesNotificationsCounter: getUserInboxesNotificationsCounter(state),
    trackCount: Object.keys(getTracks(state)).length,
    hasTracks: getHasTracks(state),
    constance: getConstanceSettings(state),
    hasBrowseSearch:
      browseFilterState !== null &&
      checkSetFilters(state.search, browseFilterState, { ignoreType: true }),
  }
}

function noDblClick(evt) {
  if (evt.detail > 1) evt.preventDefault()
}

export function indentedFolderList(
  folders: Array<GroupWithNesting>,
  folderExpanded: Record<number, boolean>,
  channelSearchTerm?: string,
  baseIndent: number = 0,
): IndentedGroup[] {
  const maxDepth = 2 // `2` = 3-levels deep

  return flatMap(folders, group => {
    const expandable = (group.folders?.length ?? 0) > 0 && baseIndent < maxDepth
    const result: IndentedGroup[] = [
      { ...group, expandable: expandable, indent: baseIndent },
    ]

    if (!expandable || !folderExpanded[group.id]) {
      return result
    } else {
      return result.concat(
        ...indentedFolderList(
          group.folders ?? [],
          folderExpanded,
          channelSearchTerm,
          baseIndent + 1,
        ),
      )
    }
  })
}

type OwnProps = {
  navigate: NavigateFunction
  bannersHeightPx: number
  sidebarOpen: boolean
  enableHeaderBlocks: boolean
  pathname: string
}

type Props = PropsFromRedux & OwnProps

class Sidebar extends React.Component<
  Props,
  {
    folderExpanded: Record<number, boolean>
    toolsExpanded: boolean
    inboxesExpanded: boolean
    discoverySuiteExpanded: boolean
    selectSidebarItem: (number | string) | null | undefined
    isRouterSidebarItem: boolean
  }
> {
  state = {
    folderExpanded: {},
    toolsExpanded: true,
    inboxesExpanded: false,
    discoverySuiteExpanded: true,
    selectSidebarItem: null,
    isRouterSidebarItem: false,
  }

  isDowngradeChannel(index) {
    const { channelsLimited } = this.props
    return channelsLimited && index > MAX_CHANNELS_LIMITED_MODE - 1
  }

  _setNotificationCounterTimestamp = (groupId: GroupId, value: string) => {
    this.props.updateUserSettings({
      settings: {
        inbox_counter_timestamps: {
          [groupId]: value,
        },
      },
    })
  }
  _clearUserInboxesNotificationsCounter = (
    event: React.SyntheticEvent<any>,
  ) => {
    event.stopPropagation()
    const groupIds = Object.keys(
      this.props.userInboxesNotificationsCounter.byId,
    ).map(Number)
    const today = new Date().toISOString()
    const timestamps = groupIds.reduce(
      (accumulator: Record<GroupId, string>, groupId: GroupId) => ({
        ...accumulator,
        [groupId]: today,
      }),
      {},
    )
    this.props.updateUserSettings({
      settings: {
        inbox_counter_timestamps: timestamps,
      },
    })
  }

  _handleInboxClick = group => {
    this._loadLibrary(group)

    const groupId = group.id

    if (typeof groupId === 'number') {
      this._setNotificationCounterTimestamp(groupId, new Date().toISOString())
    }
  }
  _renderChannelMenu = channel => (
    <GroupItemActions right compact group={channel} />
  )
  _renderChannels = (
    filterFnc: (item: IndentedGroup) => boolean,
    iconType?: string,
  ) => {
    const { folderExpanded } = this.state
    const { channelSearchTerm } = this.props
    const channels = indentedFolderList(
      this.props.channels,
      folderExpanded,
      channelSearchTerm,
    )

    if (this.props.groupsError) {
      return (
        <div className="Sidebar_Loading">
          Could not load! Please reload the page or
          <ContactUsLink>contact us.</ContactUsLink>
        </div>
      )
    }

    if (!this.props.groupsFetched) {
      // TODO: add error handling (here and in `preloadDataOnLogin`)
      return <div className="Sidebar_Loading">Loading...</div>
    }

    const folders = channels
      .filter(c => c.parent)
      .reduce((accumulator, current) => {
        let accum = accumulator
        if (!current.parent) return accum

        if (accum[current.parent]) {
          accum[current.parent].push(current)
        } else {
          accum[current.parent] = [current]
        }

        return accum
      }, {})
    let formattedСhannels = channels.filter(c => !c.parent)

    if (filterFnc) {
      formattedСhannels = formattedСhannels.filter(filterFnc)
    }

    if (this.props.channelsLimited) {
      formattedСhannels.sort((group1, group2) => {
        return group1.created > group2.created ? -1 : 1
      })
    }

    return formattedСhannels.map((c, index) =>
      this._renderChannelsItem(c, folders, index, iconType, formattedСhannels),
    )
  }
  _expandFilteredLibrary = (channelSearchTerm, channel, channelExpanded) => {
    if (!channel || !channelSearchTerm) return
    const { folderExpanded } = this.state
    const changed = Object.keys(channelExpanded ?? {}).some(
      key => folderExpanded[key] !== channelExpanded[key],
    )
    if (!changed) return
    this.setState({
      folderExpanded: { ...folderExpanded, ...channelExpanded },
    })
  }
  _renderChannelsItem = (
    channel,
    folders,
    index?: number,
    iconType?: string,
    formattedChannels?: IndentedGroup[],
  ) => {
    if (typeof index === 'number' && this.isDowngradeChannel(index)) {
      return null
    }

    const { folderExpanded, selectSidebarItem, isRouterSidebarItem } =
      this.state
    const item = (
      <CustomGroupDropWrapper
        key={`group-drop-${channel.id}`}
        group={channel}
        enableGroupDrop
        onFileHover={() => this._loadLibrary(channel)}
      >
        <SidebarItem
          key={channel.id}
          type={iconType || CUSTOM_CHANNEL}
          name={channel.name}
          compact={!this.props.sidebarOpen}
          channel={channel}
          channels={formattedChannels}
          indent={channel.indent}
          selected={!isRouterSidebarItem && selectSidebarItem === channel.id}
          expandable={channel.expandable}
          expanded={folderExpanded[channel.id]}
          menu={this._renderChannelMenu(channel)}
          channelSearchTerm={this.props.channelSearchTerm}
          onExpandFilter={this._expandFilteredLibrary}
          onExpandClick={() => {
            this.setState(state => ({
              folderExpanded: {
                ...state.folderExpanded,
                [channel.id]: !state.folderExpanded[channel.id],
              },
            }))
          }}
          onClick={() => this._loadLibrary(channel)}
        />
      </CustomGroupDropWrapper>
    )
    return (
      <React.Fragment key={channel.id}>
        {this.props.channelSort === CHANNEL_CUSTOM_SORT ? (
          <CustomGroupDragWrapper
            key={`group-drag-${channel.id}`}
            group={channel}
          >
            {item}
          </CustomGroupDragWrapper>
        ) : (
          item
        )}

        {folders[channel.id] &&
          folders[channel.id].map(c => this._renderChannelsItem(c, folders))}
      </React.Fragment>
    )
  }
  _toggleInboxesExpanded = () => {
    this.setState(prevState => ({
      inboxesExpanded: !prevState.inboxesExpanded,
    }))
  }
  _loadLibrary = lib => {
    this.setState({
      selectSidebarItem: lib.id,
    })
    const isShared = isChannelShared(
      this.props.channels.find(channel => channel.id === lib.id),
    )
    this.props.loadLibrary(lib, '', isShared)
  }

  componentDidUpdate(prevProps) {
    const { selectSidebarItem } = this.state
    const { pathname } = this.props
    let isRouterSidebarItem = false
    Object.keys(sidebarListLink).forEach(key => {
      const path = sidebarListLink[key].regexp

      if (pathname.match(path) && path !== '/') {
        isRouterSidebarItem = true
      }
    })

    const isLocationChanged = prevProps.pathname !== pathname
    const isSearchPage = pathname.includes('/search')
    const currentLocation = isSearchPage ? 'Search' : 'Explorer'
    if (isLocationChanged) {
      this.props.updatePageChangeSettings(currentLocation, {
        sidebarOpen: !isSearchPage && this.props.sidebarOpen,
      })
    }

    if (this.state.isRouterSidebarItem !== isRouterSidebarItem) {
      // TODO: refactor componentDidUpdate + this.setState
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isRouterSidebarItem,
      })
    }

    const curGroupId =
      this.props.app.selectedLibrary && this.props.app.selectedLibrary.id

    if (
      !pathname.includes('/briefs') &&
      curGroupId &&
      curGroupId !== selectSidebarItem &&
      (typeof selectSidebarItem !== 'string' || isRouterSidebarItem)
    ) {
      this.setState({
        selectSidebarItem: curGroupId,
      })
    } else if (
      pathname === '/briefs' &&
      (selectSidebarItem !== 'SIDEBAR_BRIEFS' ||
        !prevProps.pathname.includes('/briefs'))
    ) {
      this.props.loadBriefs()
      this.setState({
        selectSidebarItem: 'SIDEBAR_BRIEFS',
      })
    }

    return null
  }

  render() {
    const {
      autoTaggingUnlimitedEnabled,
      browseLibrariesEnabled,
      briefsEnabled,
      clientLibraries,
      isArtist,
      isPhone,
      liveLibrariesEnabled,
      playerVisible,
      sidebarOpen,
      userInboxesNotificationsCounter,
      constance,
    } = this.props
    const sidebarCompact = !sidebarOpen
    const { selectSidebarItem, isRouterSidebarItem } = this.state

    const territories = this.props.libraries.filter(
      l => l.group_type === TERRITORY,
    )
    const teamLib = this.props.libraries.find(
      l => l.group_type === TEAM_LIBRARY,
    )
    const userLib = this.props.libraries.find(
      l => l.group_type === PERSONAL_LIBRARY,
    )
    const newSearchEnabled = constance.ENABLE_NEW_SEARCH_PAGE
    const savedToDisco =
      constance.ENABLE_ADD_CONTENT_TO_SAVED_TO_DISCO_GROUP &&
      this.props.libraries.find(l => l.group_type === SAVED_TO_DISCO)
    const inboxes = this.props.libraries.filter(l =>
      INBOX_TYPES.includes(l.group_type),
    )
    const autoTagging = this.props.channels.find(l => l.is_auto_tagged)
    const clientLibrariesCount = Object.keys(
      clientLibraries.libraries.items,
    ).length
    const showLibrariesSidebarItem =
      browseLibrariesEnabled || isArtist
        ? liveLibrariesEnabled || clientLibrariesCount > 0
        : true
    const DSLoaded =
      clientLibraries.libraries.loaded && this.props.groupsFetched
    const discoverySuiteSidebarItem = (autoTagging || !isPhone) && (
      <SidebarTitle
        name="Discovery Suite"
        expanded={this.state.discoverySuiteExpanded}
        onClick={() => {
          this.setState({
            discoverySuiteExpanded: !this.state.discoverySuiteExpanded,
          })
        }}
      />
    )
    const browseMusicSidebarItem = browseLibrariesEnabled && (
      <SidebarSection
        id="SIDEBAR_DISCOVER_MUSIC"
        name="Discover Music"
        compact={sidebarCompact}
        selected={
          !isRouterSidebarItem && selectSidebarItem === 'SIDEBAR_DISCOVER_MUSIC'
        }
        onClick={() => {
          this.setState({
            selectSidebarItem: 'SIDEBAR_DISCOVER_MUSIC',
          })
          mixpanel.sendPureEvent('Access Discover Music')
        }}
      />
    )
    const librariesSidebarItem = showLibrariesSidebarItem && DSLoaded && (
      <SidebarSection
        id="SIDEBAR_LIBRARIES"
        name="Catalogs"
        compact={sidebarCompact}
        selected={
          !isRouterSidebarItem && selectSidebarItem === 'SIDEBAR_LIBRARIES'
        }
        onClick={() =>
          this.setState({
            selectSidebarItem: 'SIDEBAR_LIBRARIES',
          })
        }
      />
    )

    const briefsItem = briefsEnabled && (
      <SidebarSection
        id="SIDEBAR_BRIEFS"
        compact={sidebarCompact}
        tooltipLabel="Briefs"
        name="Briefs"
        selected={selectSidebarItem === 'SIDEBAR_BRIEFS'}
        onClick={() => {
          this.setState({
            selectSidebarItem: 'SIDEBAR_BRIEFS',
          })
        }}
      />
    )

    const dataCompactSidebar = sidebarCompact ? { 'data-compact': true } : {}

    return (
      <SidebarDropWrapper
        sidebarOpen={sidebarOpen}
        toggleSidebar={this.props.toggleSidebar}
      >
        <Theme appearance="dark">
          <div
            className={cn('Sidebar', {
              'Sidebar--hidden': isPhone && !sidebarOpen,
            })}
            data-testid="sidebar"
            data-player-visible={playerVisible || null}
            data-banners-height-px={this.props.bannersHeightPx}
            {...dataCompactSidebar}
            onMouseDown={noDblClick}
          >
            <CustomScrollbars
              className="Sidebar_ScrollContainer"
              color="light"
              vertical
              dndScrolling
            >
              <RadixFlex
                align="center"
                justify={sidebarCompact ? 'center' : 'between'}
                m="4"
              >
                {this.props.sidebarOpen && <SwitcherPopover />}
                <RadixTooltip
                  className="Sidebar_NavTooltip"
                  content={!this.props.sidebarOpen ? 'Expand' : 'Collapse'}
                  side="right"
                  sideOffset={
                    13
                  } /* shifts tooltip right to align with full-width nav item tooltips */
                >
                  <IconButton
                    variant="ghost"
                    color="gray"
                    onClick={this.props.toggleSidebar}
                    disabled={this.props.isOnboarding}
                    aria-label={
                      this.props.sidebarOpen ? 'Close sidebar' : 'Open sidebar'
                    }
                  >
                    {this.props.sidebarOpen ? (
                      <LuPanelLeftClose size={16} />
                    ) : (
                      <LuPanelLeftOpen size={16} />
                    )}
                  </IconButton>
                </RadixTooltip>
              </RadixFlex>

              {territories.length > 0 &&
                territories.map(territory => (
                  <SidebarItem
                    key={territory.id}
                    type={TERRITORY}
                    name={territory.name}
                    compact={sidebarCompact}
                    selected={
                      !isRouterSidebarItem && selectSidebarItem === territory.id
                    }
                    onClick={() => this._loadLibrary(territory)}
                  />
                ))}
              {teamLib && (
                <SidebarItem
                  type={TEAM_LIBRARY}
                  name={'Browse'}
                  selected={
                    !isRouterSidebarItem && selectSidebarItem === teamLib.id
                  }
                  onClick={() => {
                    if (this.props.trackCount) this.props.setIsOnboarding(false)

                    // Back from /search: keep the Browse search instead of resetting it, rerun so results match filters edited there
                    if (
                      this.props.pathname.startsWith('/search') &&
                      this.props.hasBrowseSearch &&
                      // A channel still selected needs _loadLibrary to switch back to the team library; a Browse search leaves it null
                      (!this.props.app.selectedLibrary ||
                        this.props.app.selectedLibrary.id === teamLib.id)
                    ) {
                      this.setState({ selectSidebarItem: teamLib.id })
                      this.props.navigate('/')
                      this.props.getSearchResults()
                      return
                    }

                    this._loadLibrary(teamLib)
                  }}
                  badgeCounter={
                    this.props.isOnboarding ? this.props.trackCount : 0
                  }
                  compact={sidebarCompact}
                  disabled={
                    this.props.onboardingEnabled &&
                    !this.props.hasTracks &&
                    this.props.pathname === '/'
                  }
                />
              )}
              <div
                className={cn({
                  'Sidebar_Sections--disabled': this.props.isOnboarding,
                })}
              >
                {userLib && (
                  <CustomGroupDropWrapper
                    group={userLib}
                    onFileHover={() => {}}
                  >
                    <SidebarItem
                      type={PERSONAL_LIBRARY}
                      name={'Starred'}
                      compact={sidebarCompact}
                      selected={
                        !isRouterSidebarItem && selectSidebarItem === userLib.id
                      }
                      onClick={() => this._loadLibrary(userLib)}
                    />
                  </CustomGroupDropWrapper>
                )}
                {newSearchEnabled && (
                  <SidebarSection
                    id="SIDEBAR_SEARCH_PAGE"
                    compact={sidebarCompact}
                    tooltipLabel="Search"
                    name="Search"
                    selected={this.props.pathname === '/search'}
                    onClick={() => {
                      this.setState({
                        selectSidebarItem: 'SIDEBAR_SEARCH_PAGE',
                      })
                      mixpanel.sendPureEvent('Access Search Page')
                    }}
                  />
                )}
                {savedToDisco && (
                  <SidebarItem
                    type={SAVED_TO_DISCO}
                    name={'Saved to DISCO'}
                    compact={sidebarCompact}
                    selected={
                      !isRouterSidebarItem &&
                      selectSidebarItem === savedToDisco.id
                    }
                    onClick={() => this._loadLibrary(savedToDisco)}
                  />
                )}
                <SidebarSection
                  id="SIDEBAR_INBOX"
                  name="Inboxes"
                  compact={sidebarCompact}
                  expanded={this.state.inboxesExpanded}
                  nodeRight={
                    !this.state.inboxesExpanded &&
                    userInboxesNotificationsCounter.total > 0 && (
                      <Tooltip content="Clear notifications">
                        <div
                          className={cn('Sidebar_NotificationCont', {
                            'Sidebar_NotificationCont--compact': sidebarCompact,
                          })}
                          onClick={this._clearUserInboxesNotificationsCounter}
                        >
                          <div className="Sidebar_Notification">
                            {formatNumber(
                              userInboxesNotificationsCounter.total,
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    )
                  }
                  onClick={() => {
                    // Event #1 from https://disco-team.atlassian.net/browse/DISCO-4710
                    if (!this.state.inboxesExpanded) {
                      mixpanel.sendPureEvent('Expand item in sidebar', {
                        sidebar_item: 'Inboxes',
                      })
                    }

                    if (sidebarCompact) {
                      this.props.toggleSidebar()
                    } else {
                      this._toggleInboxesExpanded()
                    }
                  }}
                />
                {!sidebarCompact && this.state.inboxesExpanded && (
                  <>
                    {this.props.groupsLoading ? (
                      <div className="Sidebar_Loading Sidebar_Loading--Poor">
                        Loading...
                      </div>
                    ) : (
                      inboxes.map(g => (
                        <SidebarItem
                          key={g.id}
                          name={g.internal_name || g.name}
                          type={g.group_type}
                          selected={
                            !isRouterSidebarItem && selectSidebarItem === g.id
                          }
                          badgeCounter={
                            userInboxesNotificationsCounter.byId[g.id]
                          }
                          showIcon={false}
                          onClick={() => this._handleInboxClick(g)}
                        />
                      ))
                    )}
                  </>
                )}

                {!autoTaggingUnlimitedEnabled && (
                  <>
                    {discoverySuiteSidebarItem}
                    {(this.state.discoverySuiteExpanded || sidebarCompact) && (
                      <>
                        {browseMusicSidebarItem}
                        {librariesSidebarItem}
                        {!DSLoaded && (
                          <div className="Sidebar_Loading Sidebar_Loading--Poor">
                            Loading...
                          </div>
                        )}
                        {autoTagging &&
                          DSLoaded &&
                          this._renderChannels(
                            item => Boolean(item.is_auto_tagged),
                            AUTO_TAGGING,
                          )}
                      </>
                    )}
                  </>
                )}

                {!isPhone && (
                  <SidebarTitle
                    name="Tools"
                    expanded={this.state.toolsExpanded}
                    onClick={() => {
                      this.setState({
                        toolsExpanded: !this.state.toolsExpanded,
                      })
                    }}
                  />
                )}
                {!isPhone && (this.state.toolsExpanded || sidebarCompact) && (
                  <>
                    {isArtist && (
                      <SidebarSection
                        id="SIDEBAR_OPPORTUNITIES"
                        name="Opportunities"
                        compact={sidebarCompact}
                        onClick={() => {
                          mixpanel.sendPureEvent('Click sidebar item', {
                            sidebar_item: 'Opportunities',
                          })
                        }}
                      />
                    )}
                    {briefsItem}
                    <SidebarSection
                      id="SIDEBAR_CONTACTS"
                      name="Contacts"
                      compact={sidebarCompact}
                    />
                    {!isArtist && (
                      <SidebarSection
                        id="SIDEBAR_EMAIL"
                        name="Email Creator"
                        compact={sidebarCompact}
                        onClick={() => {
                          mixpanel.sendPureEvent('Expand item in sidebar', {
                            sidebar_item: 'Email Creator',
                          })
                        }}
                      />
                    )}
                    <SidebarSection
                      id="SIDEBAR_LISTS"
                      name="Mailing Lists"
                      compact={sidebarCompact}
                    />
                    <SidebarSection
                      id="SIDEBAR_INSIGHTS"
                      name="Insights"
                      compact={sidebarCompact}
                    />
                    {autoTaggingUnlimitedEnabled && librariesSidebarItem}
                    <SidebarSection
                      id="SIDEBAR_WATERMARKING"
                      name="Watermarking"
                      compact={sidebarCompact}
                    />
                  </>
                )}
                {isPhone && <div className="Sidebar_Line" />}
                <SidebarChannelTitle
                  name="Channels"
                  hideSearch={!sidebarOpen}
                />
                {sidebarCompact ? (
                  <SidebarSection
                    id="CUSTOM_CHANNEL"
                    name="Channels"
                    compact={sidebarCompact}
                    onClick={this.props.toggleSidebar}
                  />
                ) : (
                  this._renderChannels(item => !item.is_auto_tagged)
                )}

                {/* <DefaultLibrariesList /> */}
                {/* <CustomLibrariesList /> */}
              </div>
              {this.props.groupsFetched && isPhone && (
                <>
                  <div className="Sidebar_Line" />
                  <Link
                    className="Sidebar_BaseItem Sidebar_Item Sidebar_Logout"
                    to="/logout"
                  >
                    Log out
                  </Link>
                </>
              )}
            </CustomScrollbars>
          </div>
        </Theme>
      </SidebarDropWrapper>
    )
  }
}

const connector = connect(mapStateToProps, {
  updateUserSettings,
  updateGroup,
  openSidebar,
  toggleSidebar,
  loadLibrary,
  loadBriefs,
  getSearchResults,
  setIsOnboarding,
  updatePageChangeSettings,
})

type PropsFromRedux = ConnectedProps<typeof connector>

const ConnectedSidebar = connector(Sidebar)

const SidebarWithNavigate = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedSidebar {...props} navigate={navigate} />
}

export default SidebarWithNavigate
